import {
  selectPermissions,
  selectPermissionsStatus,
} from '@/stores/permissionsSlice';
import { useAppSelector } from '@/stores/hooks';
import { useMemo } from 'react';

/**
 * Permission checks for the UI.
 *
 * These decide what to OFFER, never what to allow — the backend re-checks
 * every request. A user who forges a permission here gets a 403, not access.
 */
export const usePermissions = () => {
  const permissions = useAppSelector(selectPermissions);
  const status = useAppSelector(selectPermissionsStatus);

  const set = useMemo(() => new Set(permissions), [permissions]);

  return useMemo(
    () => ({
      /** All listed permissions are held. */
      can: (...required: string[]) => required.every((p) => set.has(p)),
      /** At least one is held. */
      canAny: (...required: string[]) => required.some((p) => set.has(p)),
      permissions,
      /** True once the set has actually been fetched. */
      isReady: status === 'ready',
      /**
       * Still resolving. Gate destructive UI on this so a permission-bearing
       * control does not flash absent then appear.
       */
      isLoading: status === 'loading' || status === 'idle',
    }),
    [set, permissions, status]
  );
};

export default usePermissions;
